import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield, FileText, Building2, AlertCircle,
  CheckCircle2, ArrowRight, Star, Users, Award, Clock
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import SEO from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

/* @section: home */
export default function Home() {
  const { t } = useLanguage();

  const services = [
    {
      icon: <FileText className="w-6 h-6" />,
      titleEn: "Small Business Tax Planning",
      titleKo: "스몰비즈니스 절세 플랜",
      descEn: "Year-round strategic tax planning for LLC, S-Corp, and self-employed owners—beyond just filing season.",
      descKo: "LLC, S-Corp, 자영업자를 위한 연중 절세 전략 — 단순 신고를 넘어섭니다.",
      to: "/tax-planning",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      titleEn: "Individual Tax Returns",
      titleKo: "개인 세금보고",
      descEn: "Accurate, personalized federal and state tax filing for W-2, 1099, stock, RSU/ESPP, rental, and more.",
      descKo: "W-2, 1099, 주식, RSU/ESPP, 임대 소득 등 정확하고 개인화된 세금보고.",
      to: "/individual-tax",
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      titleEn: "Business Formation",
      titleKo: "회사 설립 지원",
      descEn: "Start your business with the right tax structure. LLC, S-Corp election, EIN, and first-year tax setup.",
      descKo: "처음부터 세금 구조를 고려한 비즈니스 설립. LLC, S-Corp 선택, EIN 등.",
      to: "/business-formation",
    },
    {
      icon: <AlertCircle className="w-6 h-6" />,
      titleEn: "IRS Notices & Tax Problems",
      titleKo: "IRS 통지서 대응",
      descEn: "Enrolled Agent representation for IRS and state tax notices, balance due, and penalty review.",
      descKo: "IRS 및 주정부 세금 통지서, 미납세액, 가산세 검토를 위한 EA 대리인 서비스.",
      to: "/irs-notices",
    },
  ];

  const trustSignals = [
    { icon: <Award className="w-5 h-5" />, en: "Enrolled Agent", ko: "Enrolled Agent" },
    { icon: <Shield className="w-5 h-5" />, en: "IRS Authorized Representative", ko: "IRS 공인 대리인" },
    { icon: <Users className="w-5 h-5" />, en: "Bilingual: English & Korean", ko: "영어 & 한국어 이중언어" },
    { icon: <Star className="w-5 h-5" />, en: "Small Business Focused", ko: "스몰비즈니스 전문" },
    { icon: <Clock className="w-5 h-5" />, en: "Year-Round Support", ko: "연중 지원 서비스" },
  ];

  const painPoints = [
    {
      en: "\"My accountant only contacts me during tax season.\"",
      ko: "\"세무사가 세금보고 시즌에만 연락이 됩니다.\"",
    },
    {
      en: "\"I'm not sure if my entity structure is saving me money.\"",
      ko: "\"내 회사 구조가 절세에 유리한지 모르겠어요.\"",
    },
    {
      en: "\"I received an IRS letter and don't know what to do.\"",
      ko: "\"IRS 편지를 받았는데 어떻게 해야 할지 모르겠어요.\"",
    },
    {
      en: "\"I want a tax advisor who understands my Korean-American business.\"",
      ko: "\"한국계 비즈니스 오너를 이해하는 세무 전문가가 필요합니다.\"",
    },
  ];

  const whyEA = [
    { en: "Licensed by the IRS — the highest credential for tax professionals", ko: "IRS가 인정하는 세금 전문가 최고 자격" },
    { en: "Unlimited representation rights before the IRS", ko: "IRS 앞 무제한 대리권 보유" },
    { en: "Required to complete 72 hours of continuing education every 3 years", ko: "3년마다 72시간 의무 보수교육 이수" },
    { en: "Specialized in tax — that's all we do", ko: "세금만 전문으로 다룹니다" },
  ];

  return (
    <>
      <SEO
        title="NexBiz Tax Advisor | Small Business Tax Planning & Tax Preparation San Jose"
        description="Enrolled Agent providing Small Business Tax Planning, Tax Preparation, Business Formation, IRS Notice Assistance, and Tax Advisory Services in San Jose and the Bay Area."
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary to-[color-mix(in_srgb,var(--primary)_80%,black)] py-20 md:py-28 px-4">
        {/* Subtle gold accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-accent opacity-80" />
        <div className="container mx-auto max-w-5xl text-center relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="space-y-6"
          >
            <motion.div variants={fadeUp}>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-accent/20 text-accent border border-accent/30 mb-4">
                <Award className="w-3.5 h-3.5" />
                {t("Enrolled Agent · San Jose, CA · English & Korean", "Enrolled Agent · 샌호세, CA · 영어 & 한국어")}
              </span>
            </motion.div>
            <motion.h1
              variants={fadeUp}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground leading-tight"
            >
              {t(
                "Small Business Tax Planning & Tax Advisory",
                "스몰비즈니스 절세 전략 & 세무 서비스"
              )}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lg md:text-xl text-primary-foreground/75 max-w-3xl mx-auto leading-relaxed"
            >
              {t(
                "NexBiz Tax Advisor provides Small Business Tax Planning, Tax Preparation, Business Formation, and IRS Notice Support for clients throughout San Jose and the Bay Area. As an Enrolled Agent, we bring federal tax expertise to every engagement.",
                "NexBiz Tax Advisor는 샌호세 및 베이 지역 스몰비즈니스 고객을 위한 절세 플랜, 세금보고, 회사 설립, IRS 통지서 대응 서비스를 제공합니다. Enrolled Agent로서 모든 서비스에 연방 세금 전문성을 담습니다."
              )}
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg text-base font-semibold bg-accent text-white hover:bg-accent/90 shadow-lg hover:shadow-xl transition-all"
              >
                {t("Schedule a Consultation", "상담 예약하기")}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg text-base font-semibold border border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 transition-all"
              >
                {t("View All Services", "서비스 전체 보기")}
              </Link>
            </motion.div>
          </motion.div>
        </div>
        {/* Decorative circles */}
        <div className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full bg-accent/5 pointer-events-none" />
        <div className="absolute -top-8 -left-8 w-48 h-48 rounded-full bg-primary-foreground/3 pointer-events-none" />
      </section>

      {/* Trust Badge Strip */}
      <section className="bg-muted border-b border-border py-4 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {trustSignals.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-sm font-medium text-foreground/70">
                <span className="text-accent">{s.icon}</span>
                {t(s.en, s.ko)}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
            className="text-center mb-12"
          >
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("Helping Business Owners Reduce Taxes and Stay Compliant", "세금을 줄이고 컴플라이언스를 유지하도록 돕습니다")}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground max-w-2xl mx-auto">
              {t(
                "Our tax advisory services cover every aspect of small business tax — from Tax Preparation and Tax Planning to Business Formation and IRS Notice Support in San Jose and throughout the Bay Area.",
                "세금보고, 절세 플랜, 회사 설립, IRS 통지서 대응까지 샌호세 및 베이 지역 스몰비즈니스 세무의 모든 영역을 담당합니다."
              )}
            </motion.p>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {services.map((s, i) => (
              <motion.div key={i} variants={fadeUp}>
                <Link
                  to={s.to}
                  className="group block h-full p-6 rounded-xl border border-border bg-card hover:border-accent hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/8 flex items-center justify-center text-primary group-hover:bg-accent group-hover:text-white transition-colors shrink-0">
                      {s.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                        {t(s.titleEn, s.titleKo)}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{t(s.descEn, s.descKo)}</p>
                      <span className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-accent group-hover:gap-2 transition-all">
                        {t("Learn more", "더 알아보기")} <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="bg-muted py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-foreground text-center mb-4">
              {t("Does This Sound Familiar?", "이런 고민이 있으신가요?")}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
              {t("We hear these concerns from small business owners every day.", "스몰비즈니스 오너들이 자주 토로하는 고민들입니다.")}
            </motion.p>
            <motion.div variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {painPoints.map((p, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="flex items-start gap-3 p-5 bg-white rounded-xl border border-border shadow-sm"
                >
                  <div className="mt-0.5 w-5 h-5 rounded-full bg-accent/15 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-3.5 h-3.5 text-accent" />
                  </div>
                  <p className="text-sm text-foreground italic leading-relaxed">{t(p.en, p.ko)}</p>
                </motion.div>
              ))}
            </motion.div>
            <motion.div variants={fadeUp} className="text-center mt-8">
              <p className="text-foreground font-semibold mb-4">
                {t("NexBiz Tax Advisor is here to help.", "NexBiz Tax Advisor가 해답을 드립니다.")}
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity shadow-sm"
              >
                {t("Schedule a Free Consultation", "무료 상담 예약하기")}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Enrolled Agent */}
      <section className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={fadeUp}>
              <span className="inline-block text-xs font-semibold text-accent uppercase tracking-wider mb-3">
                {t("Why It Matters", "왜 중요한가요")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                {t("Why Work With an Enrolled Agent?", "왜 Enrolled Agent인가요?")}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {t(
                  "An Enrolled Agent (EA) is a federally-licensed tax professional authorized by the U.S. Department of the Treasury. EAs have the highest credential in the tax industry—above CPAs and attorneys specifically for tax matters.",
                  "Enrolled Agent(EA)는 미국 재무부가 인가한 연방 세금 전문가로, 세금 분야에서는 CPA나 변호사보다 높은 권한을 가진 최고 수준의 자격입니다."
                )}
              </p>
              <ul className="space-y-3">
                {whyEA.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                    <span className="text-foreground text-sm leading-relaxed">{t(item.en, item.ko)}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div variants={fadeUp}>
              <div className="bg-gradient-to-br from-primary to-[color-mix(in_srgb,var(--primary)_80%,black)] rounded-2xl p-8 text-primary-foreground">
                <div className="flex items-center gap-3 mb-6">
                  <Award className="w-8 h-8 text-accent" />
                  <div>
                    <p className="font-bold text-lg">Enrolled Agent</p>
                    <p className="text-sm text-primary-foreground/70">{t("IRS Authorized", "IRS 공인")}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="p-4 bg-white/10 rounded-xl">
                    <p className="font-semibold text-sm text-accent mb-1">{t("Bilingual Service", "이중언어 서비스")}</p>
                    <p className="text-sm text-primary-foreground/75">
                      {t("We communicate in English and Korean — no language barrier.", "영어와 한국어로 소통합니다 — 언어 장벽이 없습니다.")}
                    </p>
                  </div>
                  <div className="p-4 bg-white/10 rounded-xl">
                    <p className="font-semibold text-sm text-accent mb-1">{t("Small Business Focused", "스몰비즈니스 전문")}</p>
                    <p className="text-sm text-primary-foreground/75">
                      {t("We understand the unique challenges of Korean-American small businesses.", "한국계 미국인 스몰비즈니스의 특수한 상황을 이해합니다.")}
                    </p>
                  </div>
                  <div className="p-4 bg-white/10 rounded-xl">
                    <p className="font-semibold text-sm text-accent mb-1">{t("Year-Round Availability", "연중 지원")}</p>
                    <p className="text-sm text-primary-foreground/75">
                      {t("Tax planning happens all year, not just in April.", "절세 계획은 4월만이 아닌 1년 내내 이루어집니다.")}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-accent/8 border-t border-accent/20 py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("Ready to Make Smarter Tax Decisions?", "더 현명한 세금 결정을 시작할 준비가 되셨나요?")}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground mb-8 text-lg">
              {t(
                "Let's make your tax situation clearer. Schedule a consultation today.",
                "세금 문제, 더 명확하게 정리해드리겠습니다. 지금 상담을 예약하세요."
              )}
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity shadow-md"
              >
                {t("Schedule a Consultation", "상담 예약하기")}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg border border-primary text-primary font-semibold hover:bg-primary hover:text-primary-foreground transition-all"
              >
                {t("Explore Services", "서비스 살펴보기")}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
